import { Box, IconButton, Modal, useTheme } from '@mui/material'
import { PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'
import StyledIcon from './StyledIcon'
import layout from '../theme/layout'
import { Blocker, useResolvedPath } from 'react-router'
import RootStore from '../stores/RootStore'
import { observer } from 'mobx-react-lite'
import { AnimatePresence, motion, PanInfo, useAnimationControls, useDragControls } from 'motion/react'
import AnimatedBox from './Motion/AnimatedBox'
import Backdrop from './Backdrop'
import { easings } from './Motion/animationPresets'
import { useEscapeKeyListener } from '~/lib/hooks/useEscapeKeyListener'
import { dvhSafeStyle } from '../lib/polyFills'

interface Props extends PropsWithChildren {
  onClose: () => void
  anchor?: 'right' // TODO: Implement others?
  backControl?: 'arrow'
  scrollable?: boolean
  size?: 'small' | 'medium' | 'large'
  modalKey?: string
  disabled?: boolean
  blocker: Blocker | null
  disableDrag?: boolean
}

const ModalDrawer = (props: Props) => {
  const theme = useTheme()
  const store = useContext(RootStore)
  const { onClose, backControl = 'arrow', size = 'small', scrollable, modalKey, disabled, blocker, disableDrag } = props

  const dragControls = useDragControls()
  const animationControls = useAnimationControls()

  const modalWidth = {
    small: layout.modalDrawerWidthSmall,
    medium: layout.modalDrawerWidthMedium,
    large: layout.modalDrawerWidthLarge,
  }[size]

  const pathname = useResolvedPath('./').pathname + (modalKey ? `-${modalKey}` : '')
  const [isClosing, setIsClosing] = useState(false)

  const { openModalDrawers } = store.uiStore
  const index = [...openModalDrawers].indexOf(pathname)
  const depth = Math.max(0, [...openModalDrawers].reverse().indexOf(pathname))
  const showBackdrop = (index === 0 || (index === -1 && openModalDrawers.size === 0))

  // when the modalDrawer unmounts, we need to close the drawer by removing it from the open drawers set, even if the close animation is not triggered.
  useEffect(() => {
    return () => {
      store.uiStore.closeModalDrawer(pathname)
    }
  }, [pathname, store.uiStore])

  const dragRef = useRef(false)
  useEscapeKeyListener(onClose, depth === 0)

  const handleDragEnd = (_event: PointerEvent, info: PanInfo) => {
    if ((info.velocity.x > 200 || info.offset.x > 200) && !dragRef.current) {
      onClose()
      return
    }

    animationControls.start({ x: 0 })
  }

  useEffect(() => {
    if (!isClosing) {
      animationControls.start({ x: 0, width: modalWidth })
    }
  }, [animationControls, modalWidth, isClosing])

  const closeAnimation = { x: '100%' }

  const darken = Math.min(0.5, 0.3 * Math.log(depth + 1))

  useEffect(() => {
    if (blocker?.state === 'blocked') {
      animationControls.start({ x: 0, width: modalWidth })
    }
  }, [animationControls, modalWidth, blocker?.state])

  const closeModal = () => {
    animationControls.stop()
    setIsClosing(true)
    onClose()
  }

  return <Modal
    open={true}
    hideBackdrop
    disablePortal
    keepMounted={false}
    key={pathname}
    data-disable-drag={disableDrag}
  >
    <Box>
      <Backdrop onClose={index === -1 ? undefined : closeModal} overlayVisible={showBackdrop} />

      <motion.div
        drag='x'
        animate={animationControls}
        dragControls={dragControls}
        initial={{ ...closeAnimation, width: modalWidth }}
        exit={closeAnimation}
        transition={{ ease: easings.easeOutExpo, duration: 0.4 }}
        onAnimationStart={(animation) => {
          // Is closing
          if (JSON.stringify(animation) === JSON.stringify(closeAnimation)) {
            store.uiStore.closeModalDrawer(pathname)
          } else {
            store.uiStore.openModalDrawer(pathname)
          }
        }}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          ...dvhSafeStyle('100dvh'),
          touchAction: 'none',
          padding: theme.spacing(2),
          pointerEvents: index === -1 ? 'none' : 'auto',
          maxWidth: '97dvw',
        }}
        dragDirectionLock
        dragConstraints={{
          top: 0,
          bottom: 0,
          left: 0,
        }}
        onPanEnd={handleDragEnd}
        dragListener={false}
        onPointerDown={(e) => {
          const target = e.target as HTMLInputElement
          const isDisabled = target.closest('[data-disable-drag]')
          const isInputField = ['text', 'textarea'].includes(target?.type)
          const isChart = target.closest('[class*="MuiCharts"]') || target.closest('[class*="MuiMark"]') || target.closest('[class*="MuiLine"]')

          if (!isInputField && !isDisabled && !isChart) {
            dragControls.start(e)
            dragRef.current = false
          } else {
            dragRef.current = true
          }
        }}
      >
        {disabled && <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          borderRadius: 1,
        }} />}
        <Box sx={{
          width: '100%',
          height: '100%',
          overflowY: scrollable ? 'auto' : 'hidden',
          overflowX: 'hidden',
          borderRadius: 1,
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: theme.shadows[10],
          filter: `brightness(${1 - darken})`,
          transform: `translateX(-${depth * 20}px) scale(${1 - depth * 0.02})`,
          transition: 'all 0.3s cubic-bezier(0.22, 0.61, 0.36, 1)',
        }}>
          {props.children}
        </Box>

        <AnimatePresence>
          {depth === 0 && backControl === 'arrow' &&
            <AnimatedBox
              sx={{
                position: 'absolute',
                top: '50%',
                left: {
                  sm: theme.spacing(-5),
                  md: theme.spacing(-8),
                },
                mt: -2,
              }}
              entering={['fade', 'left']}
              exiting={['fade', 'left']}
              delayMs={100}
            >
              <IconButton onClick={closeModal} disableRipple>
                <StyledIcon name='forward' color='#ffffff' size={32} />
              </IconButton>
            </AnimatedBox>
          }
        </AnimatePresence>
      </motion.div>
    </Box>
  </Modal>
}

export default observer(ModalDrawer)
